import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { onAuthStateChanged, Unsubscribe } from 'firebase/auth';
import { authService } from '../services/auth';
import { db } from '../config/firebase';
import { AuthUser, SignInCredentials, SignUpCredentials, UserType, Role } from '../types/auth';

export interface UserPreferences {
  notifications: boolean;
  emailMarketing: boolean;
  theme: 'light' | 'dark' | 'system';
}

export interface UserStats {
  eventsCreated: number;
  eventsAttended: number;
  ticketsPurchased: number;
  totalRevenue: number;
  activeEvents: number;
  totalBookings: number;
}

export interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: Error | null;
  userRoles: Role[];
  signInWithGoogle: () => Promise<AuthUser | null>;
  signInWithEmail: (credentials: SignInCredentials) => Promise<AuthUser | null>;
  signUpWithEmail: (credentials: SignUpCredentials) => Promise<AuthUser | null>;
  signOut: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  refreshUser: () => Promise<void>;
  updateUserPreferences: (preferences: Partial<UserPreferences>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const DEFAULT_PREFERENCES: UserPreferences = {
  notifications: true,
  emailMarketing: false,
  theme: 'light',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [userRoles, setUserRoles] = useState<Role[]>([]);

  const handleAuthUser = (authUser: AuthUser | null) => {
    if (authUser) {
      setUser(authUser);
      setUserRoles(authUser.roles as Role[]);
    } else {
      setUser(null);
      setUserRoles([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authService.auth, async (firebaseUser) => {
      if (firebaseUser) {
        const authUser = await authService.getUserData(firebaseUser.uid);
        if (authUser) {
          handleAuthUser(authUser);
        } else {
          // This can happen if a user exists in Auth but their Firestore doc was deleted.
          // We'll create a new one.
          const newUser = await authService.handleMissingUser(firebaseUser);
          handleAuthUser(newUser);
        }
      } else {
        handleAuthUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async (): Promise<AuthUser | null> => {
    setLoading(true);
    try {
      const authUser = await authService.signInWithGoogle();
      handleAuthUser(authUser);
      return authUser;
    } catch (err: any) {
      setError(err);
      handleAuthUser(null);
      return null;
    }
  };

  const signInWithEmail = async (credentials: SignInCredentials): Promise<AuthUser | null> => {
    setLoading(true);
    try {
      const authUser = await authService.signInWithEmail(credentials);
      handleAuthUser(authUser);
      return authUser;
    } catch (err: any) {
      setError(err);
      handleAuthUser(null);
      return null;
    }
  };

  const signUpWithEmail = async (credentials: SignUpCredentials): Promise<AuthUser | null> => {
    setLoading(true);
    try {
      const authUser = await authService.signUpWithEmail(
        credentials.email,
        credentials.password,
        credentials.displayName,
        credentials.userType
      );
      handleAuthUser(authUser);
      return authUser;
    } catch (err: any) {
      setError(err);
      handleAuthUser(null);
      return null;
    }
  };

  const signOut = async () => {
    await authService.signOut();
    handleAuthUser(null);
  };

  const hasRole = (role: Role): boolean => {
    return userRoles.includes(role);
  };

  const refreshUser = async () => {
    const firebaseUser = authService.auth.currentUser;
    if (firebaseUser) {
      setLoading(true);
      const authUser = await authService.getUserData(firebaseUser.uid);
      handleAuthUser(authUser);
    }
  };
  
  const updateUserPreferences = useCallback((preferences: Partial<UserPreferences>) => {
    if (user) {
        authService.updateUserPreferences(user.uid, preferences);
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    userRoles,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    hasRole,
    refreshUser,
    updateUserPreferences
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;